import { useCallback, useEffect, useRef } from 'react';
import { getCsrfToken, postBeaconJSON } from './utils.js';

// Buffers viewer engagement events client-side and flushes them to the server
// every FLUSH_INTERVAL_MS, on tab visibility change, and at unload (via
// sendBeacon so the final batch survives the page death).
//
// Returns { emit(type, payload) } so callers can fire ad-hoc events for
// content sync (slide_viewed, poll_offered_seen). The hook itself wires up
// the room-lifecycle / focus / video-player / heartbeat listeners.
//
// The event taxonomy and validation live server-side; if the server doesn't
// recognize an `event_type` it silently drops the row. We don't 4xx because
// the client retries the whole batch on failure and one bad event shouldn't
// poison the batch.

const FLUSH_INTERVAL_MS = 5_000;
// Floor between ordinary requests. The server allows 10 requests per 10s per
// (user, webinar) and accepts batches of up to 50 events, so the contract is
// "few requests, many events". An immediate beacon per visibility change blows
// that budget after a handful of tab switches, and because a beacon cannot
// observe its response a 429'd batch is dropped silently. tab_hidden/tab_visible
// drive the summarizer's hidden vs focused accounting, so losing one inflates
// the student's attendance.
//
// Throttling REQUESTS (not events) keeps every event buffered until the next
// flush, so nothing compliance-significant is discarded. 1.5s floor => at most
// ~6 requests per 10s, comfortably inside the budget.
const MIN_REQUEST_INTERVAL_MS = 1_500;
const HEARTBEAT_INTERVAL_MS = 15_000;
const PLAYER_STALL_THRESHOLD_MS = 2_000;

export default function useAttendanceEvents({ endpoint, videoRef, enabled = true }) {
  const bufferRef = useRef([]);
  const sequenceRef = useRef(0);
  const flushingRef = useRef(false);
  const lastRequestAtRef = useRef(0);
  // One page exit fires beforeunload AND pagehide, and an SPA unmount runs the
  // effect cleanup. Each path emits room_exited BEFORE flushing, so the
  // empty-buffer guard cannot collapse them — without this latch a single exit
  // produces two requests and two room_exited rows.
  const exitedRef = useRef(false);

  // Stash the latest endpoint without making the emit/flush callbacks
  // re-create on every re-render (which would re-bind every listener below).
  const endpointRef = useRef(endpoint);
  useEffect(() => { endpointRef.current = endpoint; }, [endpoint]);

  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const emit = useCallback((type, payload = {}) => {
    if (!enabledRef.current) return;
    bufferRef.current.push({
      event_type: type,
      payload,
      occurred_at: new Date().toISOString(),
      client_sequence: sequenceRef.current++,
    });
  }, []);

  // `force` bypasses the request floor. Reserved for last-chance sends (unload,
  // unmount) where there is no "next flush" to fall back on.
  const flush = useCallback(async ({ viaBeacon = false, force = false } = {}) => {
    const url = endpointRef.current;
    if (!url) return;
    if (bufferRef.current.length === 0) return;

    // Too soon since the last request: leave everything buffered and let the
    // interval carry it. Skipping the REQUEST never drops an EVENT.
    if (!force && Date.now() - lastRequestAtRef.current < MIN_REQUEST_INTERVAL_MS) return;

    const batch = bufferRef.current;
    // Drain the buffer up-front; if the request fails we put them back.
    // Beacon dispatches can't observe success so we don't restore for those.
    bufferRef.current = [];
    lastRequestAtRef.current = Date.now();

    if (viaBeacon) {
      postBeaconJSON(url, { events: batch });
      return;
    }

    if (flushingRef.current) {
      // Another flush is already in flight — re-queue and let it finish.
      bufferRef.current = batch.concat(bufferRef.current);
      return;
    }
    flushingRef.current = true;
    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ events: batch }),
      });
      if (!res.ok && res.status !== 204) {
        // Re-queue for the next interval. Don't retry tight — the server may
        // be rate-limiting us (429), and hammering would prolong the throttle.
        bufferRef.current = batch.concat(bufferRef.current);
      }
    } catch (_e) {
      bufferRef.current = batch.concat(bufferRef.current);
    } finally {
      flushingRef.current = false;
    }
  }, []);

  // The one and only exit path. Idempotent: whichever of beforeunload /
  // pagehide / unmount arrives first sends the final batch; the rest no-op.
  const finalizeExit = useCallback(() => {
    if (exitedRef.current) return;
    exitedRef.current = true;
    emit('room_exited');
    // Forced: this is the last chance to send, so it bypasses the request floor.
    flush({ viaBeacon: true, force: true });
  }, [emit, flush]);

  // room_entered / room_exited + periodic flush + heartbeat
  useEffect(() => {
    if (!enabled) return;
    // A fresh mount (or re-enable) is a fresh session and may exit again.
    exitedRef.current = false;
    emit('room_entered');

    const flushTimer = setInterval(() => { flush(); }, FLUSH_INTERVAL_MS);
    const heartbeatTimer = setInterval(() => {
      if (document.visibilityState === 'visible') emit('heartbeat');
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(flushTimer);
      clearInterval(heartbeatTimer);
      // Covers SPA navigation, where no unload event fires at all.
      finalizeExit();
    };
  }, [enabled, emit, flush, finalizeExit]);

  // Tab visibility — hidden when minimized OR a different browser tab is on top.
  useEffect(() => {
    if (!enabled) return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        emit('tab_hidden');
        // Try to flush immediately — the tab might be on the way to being
        // killed. Throttled, not forced: if we just sent, the events stay
        // buffered and pagehide/unmount still force a final send.
        flush({ viaBeacon: true });
      } else {
        emit('tab_visible');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enabled, emit, flush]);

  // Window focus — different from visibility: alt-tabbing to another app blurs
  // the window but the tab can still be visible. Together they give us a
  // three-way split (focused / blurred / hidden) in the summarizer.
  useEffect(() => {
    if (!enabled) return;
    const onBlur  = () => emit('window_blurred');
    const onFocus = () => emit('window_focused');
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, emit]);

  // Unload — final flush via beacon. pagehide fires on bfcache eviction too,
  // beforeunload covers the classic case.
  useEffect(() => {
    if (!enabled) return;
    window.addEventListener('pagehide', finalizeExit);
    window.addEventListener('beforeunload', finalizeExit);
    return () => {
      window.removeEventListener('pagehide', finalizeExit);
      window.removeEventListener('beforeunload', finalizeExit);
    };
  }, [enabled, finalizeExit]);

  // Video player events — instrumented on the <video> ref the parent passes in.
  // Stalls are tracked with a timer so we only emit `player_stalled` when the
  // buffer actually empties for longer than PLAYER_STALL_THRESHOLD_MS; the
  // raw `stalled` event fires on every minor hiccup.
  useEffect(() => {
    if (!enabled) return;
    const v = videoRef?.current;
    if (!v) return;
    let stallTimer = null;
    const onPlay  = () => emit('player_play');
    const onPause = () => emit('player_pause');
    const onError = () => emit('player_error');
    const onWaiting = () => {
      if (stallTimer) return;
      stallTimer = setTimeout(() => {
        emit('player_stalled');
        stallTimer = null;
      }, PLAYER_STALL_THRESHOLD_MS);
    };
    const onPlaying = () => {
      if (stallTimer) { clearTimeout(stallTimer); stallTimer = null; }
    };
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('error', onError);
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('playing', onPlaying);
    return () => {
      if (stallTimer) clearTimeout(stallTimer);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('error', onError);
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('playing', onPlaying);
    };
  }, [enabled, emit, videoRef]);

  return { emit };
}
